import type { ReactNode } from 'react'
import { ChevronsLeft, ChevronsRight, History, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

export type ScreenerSidebarTab = 'discovery' | 'history'

export function ScreenerSidebar({ open, onOpenChange, tab, onTabChange, historyCount, discovery, history }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  tab: ScreenerSidebarTab
  onTabChange: (tab: ScreenerSidebarTab) => void
  historyCount: number
  discovery: ReactNode
  history: ReactNode
}) {
  return (
    <aside aria-label="Ticker panel" className="flex min-h-0 min-w-0 w-full flex-col bg-[#070b12]">
      {!open && (
        <div className="flex flex-1 flex-col items-center border-r border-slate-800/80 py-2">
          <Button type="button" size="icon" variant="ghost" onClick={() => onOpenChange(true)}
            className="h-8 w-8 text-slate-500 hover:text-teal-300" title="Open ticker panel" aria-label="Open ticker panel">
            <ChevronsRight className="h-4 w-4" />
          </Button>
          <div className="mt-4 rotate-180 text-[9px] font-semibold uppercase tracking-[0.22em] text-slate-600 [writing-mode:vertical-rl]">
            {tab === 'history' ? 'Ticker history' : 'OI discovery'}
          </div>
        </div>
      )}
      <Tabs value={tab} onValueChange={(value) => onTabChange(value as ScreenerSidebarTab)}
        className={open ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>
        <div className="flex items-center gap-1 border-b border-slate-800/80 p-2">
          <TabsList aria-label="Ticker lists" className="h-8 min-w-0 flex-1 bg-slate-950/60">
            <TabsTrigger value="discovery" className="h-7 flex-1 gap-1.5 px-2 text-[11px] data-[state=active]:text-teal-300">
              <Search className="h-3 w-3" /> Discovery
            </TabsTrigger>
            <TabsTrigger value="history" className="h-7 flex-1 gap-1.5 px-2 text-[11px] data-[state=active]:text-teal-300">
              <History className="h-3 w-3" /> History <span className="text-[9px] text-slate-500">{historyCount}</span>
            </TabsTrigger>
          </TabsList>
          <Button type="button" size="icon" variant="ghost" onClick={() => onOpenChange(false)}
            className="h-7 w-7 shrink-0 text-slate-600 hover:text-slate-200" title="Hide ticker panel" aria-label="Hide ticker panel">
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        </div>
        <TabsContent value="discovery" forceMount className="m-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">{discovery}</TabsContent>
        <TabsContent value="history" forceMount className="m-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">{history}</TabsContent>
      </Tabs>
    </aside>
  )
}
